/**
 * Limits and checks for a requirement's text and metadata, mirroring the
 * backend (buf.validate rules in requirements.proto and validateTags in
 * update_thread_metadata.go) so the form refuses what the server would.
 */

export const NAME_MAX = 255;
export const EXTERNAL_REF_MAX = 255;
export const DESCRIPTION_MAX = 4000;
export const OWNER_MAX = 255;
export const TEXT_MAX = 65536;
export const TAGS_MAX = 50;
export const TAG_MAX = 256;

// Letters and numbers in any script, spaces, `_ . : / = + - @`, and the
// full-width punctuation a Japanese IME inserts by default (ideographic space
// U+3000, ： ・ ， 、 。 （ ）). Built from a string so the escapes survive
// formatting: a literal U+3000 trips no-irregular-whitespace.
const TAG_ALLOWED = new RegExp(
  '^[\\p{L}\\p{N} _.:/=+\\-@\\u3000\\uFF1A\\u30FB\\uFF0C\\u3001\\u3002\\uFF08\\uFF09]+$',
  'u',
);

/**
 * Length in characters (code points), as the backend counts it. A JavaScript
 * string's `length` counts UTF-16 units, so an emoji would count twice.
 */
export function charCount(value: string): number {
  return Array.from(value).length;
}

export interface MetadataValues {
  name: string;
  externalRef: string;
  description: string;
  owner: string;
  tags: string[];
}

/** A field's problem, as an i18n key under `detail.metadata.errors` and its params. */
export interface FieldError {
  key: string;
  params?: Record<string, unknown>;
}

export type MetadataErrors = Partial<Record<keyof MetadataValues, FieldError>>;

function tooLong(value: string, max: number): FieldError | undefined {
  return charCount(value) > max
    ? { key: 'tooLong', params: { max } }
    : undefined;
}

/** Returns the problem with a tag, or undefined if the backend would accept it. */
export function tagError(
  tag: string,
  others: string[],
): FieldError | undefined {
  if (charCount(tag) > TAG_MAX) {
    return { key: 'tagTooLong', params: { tag, max: TAG_MAX } };
  }
  if (!TAG_ALLOWED.test(tag)) {
    return { key: 'tagInvalidChars', params: { tag } };
  }
  if (others.includes(tag)) {
    return { key: 'tagDuplicate', params: { tag } };
  }
  return undefined;
}

/** Checks the values the form would save. Empty object = valid. */
export function validateMetadata(values: MetadataValues): MetadataErrors {
  const all: MetadataErrors = {
    name:
      values.name.trim() === ''
        ? { key: 'nameRequired' }
        : tooLong(values.name, NAME_MAX),
    externalRef: tooLong(values.externalRef, EXTERNAL_REF_MAX),
    description: tooLong(values.description, DESCRIPTION_MAX),
    owner: tooLong(values.owner, OWNER_MAX),
    tags:
      values.tags.length > TAGS_MAX
        ? { key: 'tooManyTags', params: { max: TAGS_MAX } }
        : values.tags
            .map((tag, i) => tagError(tag, values.tags.slice(0, i)))
            .find(Boolean),
  };
  return Object.fromEntries(
    Object.entries(all).filter(([, error]) => error !== undefined),
  ) as MetadataErrors;
}

/** The fields that changed, ready for `updateThreadMetadata`. */
export function changedMetadata(
  before: MetadataValues,
  after: MetadataValues,
): Partial<MetadataValues> {
  const patch: Partial<MetadataValues> = {};
  if (after.name !== before.name) patch.name = after.name;
  if (after.externalRef !== before.externalRef) {
    patch.externalRef = after.externalRef;
  }
  if (after.description !== before.description) {
    patch.description = after.description;
  }
  if (after.owner !== before.owner) patch.owner = after.owner;
  // Tags cannot contain U+0000, so joining on it compares the lists exactly.
  if (after.tags.join('\u0000') !== before.tags.join('\u0000')) {
    patch.tags = after.tags;
  }
  return patch;
}
